import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { registerPolling, POLL_FALLBACK_SLOW } from '@/hooks/usePolling';

/**
 * 通知ベルに表示する通知種別の ON/OFF 設定フック。
 * ユーザー単位で Supabase（notification_settings）に永続化し、端末間で同期する。
 *
 * 種別は以下の9種。既定はすべて表示。
 *  - messages       新着メッセージ
 *  - consultation   相談（窓口からの返信／新着相談）
 *  - dailyReport    日報
 *  - project        プロジェクト（タスク割当・コメント）
 *  - task           処理完了
 *  - taskReminder   タスクのリマインド（締切前・締切超過）
 *  - activity       アクティビティ（コメント・リアクション・アンケート）
 *  - announcement   お知らせ
 *  - schedule       予定リマインド
 */
export type NotificationType =
  | 'messages'
  | 'consultation'
  | 'dailyReport'
  | 'project'
  | 'task'
  | 'taskReminder'
  | 'activity'
  | 'announcement'
  | 'schedule'
  | 'itSupport'
  | 'systemHealth';

export interface NotificationTypeMeta {
  key: NotificationType;
  label: string;
  icon: string;
  /** マウスオーバー時に表示する「何の通知か」の説明 */
  description: string;
}

export const NOTIFICATION_TYPES: NotificationTypeMeta[] = [
  {
    key: 'messages',
    label: '新着メッセージ',
    icon: 'ri-mail-line',
    description: '新しいメッセージが届いたときに通知します',
  },
  {
    key: 'consultation',
    label: '相談・返信',
    icon: 'ri-question-answer-line',
    description: '相談の新着や窓口からの返信があったときに通知します',
  },
  {
    key: 'dailyReport',
    label: '日報',
    icon: 'ri-file-list-3-line',
    description: '日報の確認・全員提出・未提出リマインドの通知を制御します',
  },
  {
    key: 'project',
    label: 'プロジェクト',
    icon: 'ri-projector-line',
    description: 'プロジェクトのタスク割当・コメントを通知します',
  },
  {
    key: 'task',
    label: '処理完了',
    icon: 'ri-clipboard-line',
    description: '作業や各種処理が完了したときに通知します',
  },
  {
    key: 'taskReminder',
    label: 'タスクのリマインド',
    icon: 'ri-alarm-line',
    description: 'タスクの締切前（30分前）と締切超過を通知します',
  },
  {
    key: 'activity',
    label: 'アクティビティ',
    icon: 'ri-chat-3-line',
    description: 'コメント・リアクション・アンケート回答を通知します',
  },
  {
    key: 'announcement',
    label: 'お知らせ',
    icon: 'ri-megaphone-line',
    description: '新しいお知らせが投稿されたときに通知します',
  },
  {
    key: 'schedule',
    label: '予定リマインド',
    icon: 'ri-calendar-event-line',
    description: '予定の開始前にリマインドします',
  },
  {
    key: 'itSupport',
    label: 'ITサポート',
    icon: 'ri-customer-service-2-line',
    description: 'ITサポートの申請・受付を通知します',
  },
  {
    key: 'systemHealth',
    label: 'システム健康度',
    icon: 'ri-heart-pulse-line',
    description: 'システムの異常・復旧を通知します（IT開発者向け）',
  },
];

const DEFAULT_SETTINGS: Record<NotificationType, boolean> = {
  messages: true,
  consultation: true,
  dailyReport: true,
  project: true,
  task: true,
  taskReminder: true,
  activity: true,
  announcement: true,
  schedule: true,
  itSupport: true,
  systemHealth: true,
};

/** 朝の「今日の予定」ダイジェストの既定通知時刻（JST）。 */
export const DEFAULT_DIGEST_TIME = '08:00';

/**
 * 朝のリマインド通知（今日の予定ダイジェスト＋朝のタスク／日報などのリマインド）の設定値。
 * - true  : ON（送る）
 * - false : OFF（送らない）
 * - null  : 未設定（初回ポップアップで選ばせる。送信側は送らない＝明示オプトイン）
 */
export type MorningReminderSetting = boolean | null;

/** HH:mm 形式に正規化する（不正な値は既定時刻）。 */
export const normalizeDigestTime = (value: unknown): string => {
  if (typeof value !== 'string') return DEFAULT_DIGEST_TIME;
  return /^([01]\d|2[0-3]):([0-5]\d)$/.test(value) ? value : DEFAULT_DIGEST_TIME;
};

async function persistSettings(
  userId: string,
  settings: Record<NotificationType, boolean>,
  digestTime: string,
  morningReminder: MorningReminderSetting,
  itSupportSound: boolean
): Promise<void> {
  try {
    const { error } = await supabase.from('notification_settings').upsert(
      {
        user_id: userId,
        settings: {
          ...settings,
          scheduleDigestTime: digestTime,
          morningReminder,
          itSupportSound,
        },
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id' }
    );
    if (error) {
      console.error('通知設定の保存に失敗:', error.message);
    }
  } catch (error) {
    console.error('通知設定の保存に失敗:', error);
  }
}

/** ITサポートの空き通知の音（アプリ内チャイム）の既定値。 */
export const DEFAULT_IT_SUPPORT_SOUND = true;

export function useNotificationSettings(userId: string | undefined) {
  const [settings, setSettings] = useState<Record<NotificationType, boolean>>(() => ({
    ...DEFAULT_SETTINGS,
  }));
  const [digestTime, setDigestTime] = useState<string>(DEFAULT_DIGEST_TIME);
  const [morningReminder, setMorningReminderState] = useState<MorningReminderSetting>(null);
  // ITサポートの空き通知の音（アプリ内チャイム）を鳴らすかどうか。
  const [itSupportSound, setItSupportSoundState] = useState<boolean>(DEFAULT_IT_SUPPORT_SOUND);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    if (!userId) {
      setSettings({ ...DEFAULT_SETTINGS });
      setDigestTime(DEFAULT_DIGEST_TIME);
      setMorningReminderState(null);
      setItSupportSoundState(DEFAULT_IT_SUPPORT_SOUND);
      setLoaded(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('notification_settings')
        .select('settings')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;

      const stored = (data?.settings as Record<string, unknown>) || {};
      const {
        scheduleDigestTime,
        morningReminder: storedMorningReminder,
        itSupportSound: storedItSupportSound,
        ...booleanStored
      } = stored;
      setSettings({
        ...DEFAULT_SETTINGS,
        ...(booleanStored as Partial<Record<NotificationType, boolean>>),
      });
      setDigestTime(normalizeDigestTime(scheduleDigestTime));
      setMorningReminderState(
        typeof storedMorningReminder === 'boolean' ? storedMorningReminder : null
      );
      setItSupportSoundState(
        typeof storedItSupportSound === 'boolean' ? storedItSupportSound : DEFAULT_IT_SUPPORT_SOUND
      );
    } catch (error) {
      console.error('通知設定の取得エラー:', error);
      setSettings({ ...DEFAULT_SETTINGS });
      setDigestTime(DEFAULT_DIGEST_TIME);
      setMorningReminderState(null);
      setItSupportSoundState(DEFAULT_IT_SUPPORT_SOUND);
    } finally {
      setLoaded(true);
    }
  }, [userId]);

  useEffect(() => {
    load();

    // 設定は更新頻度が低いため、ポーリング間隔を伸ばして負荷を抑える。
    const unregister = registerPolling(load, POLL_FALLBACK_SLOW);

    return () => {
      unregister();
    };
  }, [load]);

  const isEnabled = useCallback(
    (type: NotificationType) => settings[type] !== false,
    [settings]
  );

  const toggle = useCallback(
    (type: NotificationType) => {
      if (!userId) return;
      const next = { ...settings, [type]: !settings[type] };
      setSettings(next);
      persistSettings(userId, next, digestTime, morningReminder, itSupportSound);
    },
    [userId, settings, digestTime, morningReminder, itSupportSound]
  );

  const updateDigestTime = useCallback(
    (value: string) => {
      if (!userId) return;
      const normalized = normalizeDigestTime(value);
      setDigestTime(normalized);
      persistSettings(userId, settings, normalized, morningReminder, itSupportSound);
    },
    [userId, settings, morningReminder, itSupportSound]
  );

  // 朝のリマインド通知の ON/OFF を保存する（未設定＝null から初回に true/false を選ばせる）。
  const setMorningReminder = useCallback(
    (value: boolean) => {
      if (!userId) return;
      setMorningReminderState(value);
      persistSettings(userId, settings, digestTime, value, itSupportSound);
    },
    [userId, settings, digestTime, itSupportSound]
  );

  // ITサポートの空き通知の音（アプリ内チャイム）の ON/OFF を保存する。
  const toggleItSupportSound = useCallback(() => {
    if (!userId) return;
    const next = !itSupportSound;
    setItSupportSoundState(next);
    persistSettings(userId, settings, digestTime, morningReminder, next);
  }, [userId, settings, digestTime, morningReminder, itSupportSound]);

  return {
    settings,
    loaded,
    isEnabled,
    toggle,
    digestTime,
    updateDigestTime,
    morningReminder,
    morningReminderConfigured: morningReminder !== null,
    setMorningReminder,
    itSupportSound,
    toggleItSupportSound,
    refresh: load,
  };
}